<script>
    import Dropdown from './DropDownComponent.svelte';
    import ExerciseBankSidebar from './ExerciseBankSidebar.svelte';
    import { onMount, onDestroy } from 'svelte';
    import { MathfieldElement } from 'mathlive';
    import katex from 'katex';
    import { push } from 'svelte-spa-router';
    import { subjectOptions, usesMathEditor } from './subjectCatalog.js';

    // Sidebar navigation
    let activePage = 'ikelti';

    function handlePageChange(pageId) {
        if (pageId === 'ikelti') {
            // Already on upload page, do nothing or refresh
            return;
        } else {
            // Navigate to other pages
            if (pageId === 'pradzia') {
                push('/exercise-bank');
            } else if (pageId === 'uzduociu-bankas') {
                push('/exercise-bank/bank');
            } else {
                activePage = pageId;
            }
        }
    }

    // Source form data
    let sourceType = '';
    let sourceName = '';
    let sourceDetails = {
        // For textbooks
        chapter: '',
        page_number: '',
        // For exams
        year: '',
        semester: '',
        institution: '',
        // For custom/other
        notes: ''
    };

    // Source type options
    const sourceTypeOptions = [
        ['exam', 'Exam'],
        ['textbook', 'Textbook'],
        ['custom', 'Custom'],
        ['worksheet', 'Worksheet'],
        ['other', 'Other']
    ];

    // Computed property to determine which detail fields to show
    $: showTextbookFields = sourceType === 'textbook';
    $: showExamFields = sourceType === 'exam';
    $: showCustomFields = sourceType === 'custom' || sourceType === 'other' || sourceType === 'worksheet';

    // Reset details when source type changes
    $: if (sourceType) {
        // Clear all details when type changes
        sourceDetails = {
            chapter: '',
            page_number: '',
            year: '',
            semester: '',
            institution: '',
            notes: ''
        };
    }

    // Exercise form data
    let questionType = '';
    let subject = '';
    let grade = '';
    let questionText = '';
    let difficulty = '';
    let topicsInput = ''; // Comma-separated string input
    let reviewed = false;
    
    // Type-specific metadata
    let exerciseMetadata = {};
    
    // For Multiple Choice Questions
    let mcqOptions = [{ text: '', is_correct: false }];
    let allowMultiple = false;
    let mcqMarks = '';
    
    // For Fill-in-the-Blank
    let fillBlankAnswers = [''];
    let fillBlankMarks = [''];
    
    // For Matching
    let matchingPairs = [{ left: '', right: '' }];
    
    // For Free Form / True False
    let correctAnswer = '';
    let marks = '';
    let hints = '';
    let trueFalseAnswer = ''; // Will be 'true' or 'false' as string
    
    // Exercise Images
    let exerciseImages = []; // Array of { file: File, caption: '', preview: '' }
    
    // Question type options
    const questionTypeOptions = [
        ['free_form', 'Free Form'],
        ['mcq', 'Multiple Choice Question'],
        ['fill_blank', 'Fill in the Blank'],
        ['true_false', 'True/False'],
        ['matching', 'Matching'],
        ['other', 'Other']
    ];
    
    // Difficulty options
    const difficultyOptions = [
        ['easy', 'Easy'],
        ['medium', 'Medium'],
        ['hard', 'Hard']
    ];
    
    // Grade options
    const gradeOptions = [
        ['1', '1'],
        ['2', '2'],
        ['3', '3'],
        ['4', '4'],
        ['5', '5'],
        ['6', '6'],
        ['7', '7'],
        ['8', '8'],
        ['9', '9'],
        ['10', '10'],
        ['11', '11'],
        ['12', '12'],
        ['university', 'University Level']
    ];
    
    // Computed properties for type-specific fields
    $: showFreeFormFields = questionType === 'free_form';
    $: showMcqFields = questionType === 'mcq';
    $: showFillBlankFields = questionType === 'fill_blank';
    $: showMatchingFields = questionType === 'matching';
    $: showTrueFalseFields = questionType === 'true_false';
    
    // Computed property to determine if MathLive should be used
    $: useMathEditor = subject && usesMathEditor(subject);
    
    // MathLive slide-in panel state
    let showMathPanel = false;
    let mathPanelValue = '';
    let questionTextareaRef;
    let mathPanelFieldElement;
    let cursorPosition = 0;
    let keyboardOpened = false; // Track if keyboard has been opened
    let keyboardObserver = null; // MutationObserver for keyboard
    
    // Initialize MathLive when component mounts
    onMount(() => {
        // Register the custom element if not already registered
        if (!customElements.get('math-field')) {
            customElements.define('math-field', MathfieldElement);
        }
    });
    
    // Function to open math panel and store cursor position
    function openMathPanel() {
        if (questionTextareaRef) {
            // Use selectionStart (start of selection) or cursor position
            cursorPosition = questionTextareaRef.selectionStart !== undefined 
                ? questionTextareaRef.selectionStart 
                : questionText.length;
        } else {
            cursorPosition = questionText.length;
        }
        mathPanelValue = '';
        keyboardOpened = false; // Reset flag
        showMathPanel = true;
        // Set up observer to watch for keyboard
        setupKeyboardObserver();
    }
    
    // Function to close math panel
    function closeMathPanel() {
        // Close keyboard if open
        if (mathPanelFieldElement) {
            try {
                mathPanelFieldElement.executeCommand('toggleVirtualKeyboard');
            } catch (e) {
                // Keyboard might already be closed, ignore error
            }
        }
        keyboardOpened = false; // Reset flag
        showMathPanel = false;
        mathPanelValue = '';
        // Disconnect observer
        if (keyboardObserver) {
            keyboardObserver.disconnect();
            keyboardObserver = null;
        }
    }
    
    
    // Function to insert math into textarea at cursor position
    function insertMathIntoTextarea() {
        if (!mathPanelFieldElement) return;
        
        const latexValue = mathPanelFieldElement.getValue();
        if (!latexValue.trim()) {
            closeMathPanel();
            return;
        }
        
        // Wrap in $ delimiters and add spaces
        const mathToInsert = ` $${latexValue}$ `;
        
        if (questionTextareaRef) {
            const text = questionText;
            const before = text.substring(0, cursorPosition);
            const after = text.substring(cursorPosition);
            questionText = before + mathToInsert + after;
            
            // Set cursor position after inserted math
            setTimeout(() => {
                if (questionTextareaRef) {
                    const newPosition = cursorPosition + mathToInsert.length;
                    questionTextareaRef.setSelectionRange(newPosition, newPosition);
                    questionTextareaRef.focus();
                }
            }, 0);
        } else {
            // Fallback: append to end
            questionText = questionText + mathToInsert;
        }
        
        closeMathPanel();
    }
    
    // Function to open keyboard in panel
    function openKeyboardInPanel() {
        if (!keyboardOpened && mathPanelFieldElement && showMathPanel) {
            keyboardOpened = true;
            // Wait for panel animation and element to be ready
            setTimeout(() => {
                if (mathPanelFieldElement && showMathPanel) {
                    mathPanelFieldElement.focus();
                    // Open keyboard after focus
                    setTimeout(() => {
                        try {
                            mathPanelFieldElement.executeCommand('toggleVirtualKeyboard');
                            // Force keyboard z-index after it opens
                            setTimeout(() => {
                                forceKeyboardZIndex();
                            }, 200);
                        } catch (e) {
                            console.log('Keyboard toggle error:', e);
                        }
                    }, 100);
                }
            }, 350);
        }
    }
    
    // Function to force keyboard z-index to be above modal
    function forceKeyboardZIndex() {
        // Try multiple selectors to find the keyboard
        const selectors = [
            'math-virtual-keyboard',
            '[data-mathlive-virtual-keyboard]',
            '.ML__keyboard',
            'math-field math-virtual-keyboard'
        ];
        
        selectors.forEach(selector => {
            const keyboards = document.querySelectorAll(selector);
            keyboards.forEach(keyboard => {
                if (keyboard instanceof HTMLElement) {
                    keyboard.style.zIndex = '2147483647';
                    keyboard.style.position = 'fixed';
                    keyboard.style.bottom = '0';
                    keyboard.style.left = '0';
                    keyboard.style.right = '0';
                }
            });
        });
        
        // Also check shadow DOMs
        const mathFields = document.querySelectorAll('math-field');
        mathFields.forEach(field => {
            if (field.shadowRoot) {
                const shadowKeyboard = field.shadowRoot.querySelector('math-virtual-keyboard, .ML__keyboard');
                if (shadowKeyboard) {
                    shadowKeyboard.style.zIndex = '2147483647';
                }
            }
        });
    }
    
    // Set up MutationObserver to watch for keyboard appearance
    function setupKeyboardObserver() {
        if (keyboardObserver) {
            keyboardObserver.disconnect();
        }
        
        keyboardObserver = new MutationObserver(() => {
            if (showMathPanel) {
                forceKeyboardZIndex();
            }
        });
        
        // Observe body for keyboard additions
        keyboardObserver.observe(document.body, {
            childList: true,
            subtree: true
        });
    }
    
    // Function to handle math panel field mount
    function handleMathPanelFieldMount(element) {
        if (element) {
            mathPanelFieldElement = element;
            if (mathPanelValue) {
                element.setValue(mathPanelValue);
            }
            // When field is mounted and panel is open, open keyboard
            if (showMathPanel) {
                openKeyboardInPanel();
            }
        }
    }
    
    // Reactive statement to open keyboard when panel becomes visible and element exists
    $: if (showMathPanel && mathPanelFieldElement && !keyboardOpened) {
        openKeyboardInPanel();
    }
    
    // Function to render LaTeX math in preview
    function renderMathPreview(text) {
        if (!text) return [{ type: 'text', content: '' }];
        
        // Replace $...$ with rendered math
        const mathRegex = /\$([^$]+)\$/g;
        const parts = [];
        let lastIndex = 0;
        let match;
        
        while ((match = mathRegex.exec(text)) !== null) {
            // Add text before math
            if (match.index > lastIndex) {
                const textBefore = text.substring(lastIndex, match.index);
                if (textBefore) {
                    parts.push({ type: 'text', content: textBefore });
                }
            }
            
            // Add rendered math
            try {
                const rendered = katex.renderToString(match[1], {
                    throwOnError: false,
                    displayMode: false
                });
                parts.push({ type: 'math', content: rendered });
            } catch (e) {
                // If rendering fails, show original
                parts.push({ type: 'text', content: match[0] });
            }
            
            lastIndex = match.index + match[0].length;
        }
        
        // Add remaining text
        if (lastIndex < text.length) {
            const remainingText = text.substring(lastIndex);
            if (remainingText) {
                parts.push({ type: 'text', content: remainingText });
            }
        }
        
        return parts.length > 0 ? parts : [{ type: 'text', content: text }];
    }
    
    // Reactive preview content
    $: previewParts = renderMathPreview(questionText);
    
    // Reset metadata when question type changes
    $: if (questionType) {
        // Clear all metadata when type changes
        exerciseMetadata = {};
        mcqOptions = [{ text: '', is_correct: false }];
        allowMultiple = false;
        mcqMarks = '';
        fillBlankAnswers = [''];
        fillBlankMarks = [''];
        matchingPairs = [{ left: '', right: '' }];
        correctAnswer = '';
        marks = '';
        hints = '';
        trueFalseAnswer = '';
    }
    
    // MCQ Functions
    function addMcqOption() {
        mcqOptions = [...mcqOptions, { text: '', is_correct: false }];
    }
    
    function removeMcqOption(index) {
        mcqOptions = mcqOptions.filter((_, i) => i !== index);
    }
    
    function toggleMcqCorrect(index) {
        if (!allowMultiple) {
            // If single choice, uncheck all others
            mcqOptions = mcqOptions.map((opt, i) => ({
                ...opt,
                is_correct: i === index ? !opt.is_correct : false
            }));
        } else {
            // If multiple choice, toggle this one
            mcqOptions[index].is_correct = !mcqOptions[index].is_correct;
            mcqOptions = [...mcqOptions]; // Trigger reactivity
        }
    }
    
    // Fill-in-the-Blank Functions
    function addFillBlank() {
        fillBlankAnswers = [...fillBlankAnswers, ''];
        fillBlankMarks = [...fillBlankMarks, ''];
    }
    
    function removeFillBlank(index) {
        fillBlankAnswers = fillBlankAnswers.filter((_, i) => i !== index);
        fillBlankMarks = fillBlankMarks.filter((_, i) => i !== index);
    }
    
    // Matching Functions
    function addMatchingPair() {
        matchingPairs = [...matchingPairs, { left: '', right: '' }];
    }
    
    function removeMatchingPair(index) {
        matchingPairs = matchingPairs.filter((_, i) => i !== index);
    }
    
    // Image Functions
    function handleImageUpload(event) {
        const files = Array.from(event.target.files);
        files.forEach(file => {
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    exerciseImages = [...exerciseImages, {
                        file: file,
                        caption: '',
                        preview: e.target.result
                    }];
                };
                reader.readAsDataURL(file);
            }
        });
        // Reset input to allow selecting the same file again
        event.target.value = '';
    }
    
    function removeImage(index) {
        exerciseImages = exerciseImages.filter((_, i) => i !== index);
    }
    
    // Submit state
    let isSubmitting = false;
    let submitError = '';
    let submitSuccess = '';
    
    // Submit handler function
    async function handleSubmit() {
        // Reset messages
        submitError = '';
        submitSuccess = '';
        
        // Basic validation
        if (!sourceType || !sourceName) {
            submitError = 'Please fill in all required source fields';
            return;
        }
        
        if (!questionType || !subject || !grade || !questionText || !difficulty || !topicsInput) {
            submitError = 'Please fill in all required exercise fields';
            return;
        }
        
        // Validate type-specific fields
        if (questionType === 'mcq' && (!mcqOptions || mcqOptions.length === 0 || mcqOptions.some(opt => !opt.text))) {
            submitError = 'Please add at least one MCQ option with text';
            return;
        }
        
        if (questionType === 'fill_blank' && (!fillBlankAnswers || fillBlankAnswers.length === 0 || fillBlankAnswers.some(a => !a))) {
            submitError = 'Please add at least one fill-in-the-blank answer';
            return;
        }
        
        if (questionType === 'matching' && (!matchingPairs || matchingPairs.length === 0 || matchingPairs.some(p => !p.left || !p.right))) {
            submitError = 'Please add at least one matching pair with both left and right items';
            return;
        }
        
        if (questionType === 'true_false' && !trueFalseAnswer) {
            submitError = 'Please select True or False';
            return;
        }
        
        isSubmitting = true;
        
        try {
            // Structure metadata based on question type
            let metadata = {};
            
            if (questionType === 'free_form') {
                metadata = {
                    correct_answer: correctAnswer,
                    marks: marks,
                    hints: hints
                };
            } else if (questionType === 'mcq') {
                metadata = {
                    allow_multiple: allowMultiple,
                    marks: mcqMarks,
                    options: mcqOptions.map(opt => ({
                        text: opt.text,
                        is_correct: opt.is_correct
                    }))
                };
            } else if (questionType === 'fill_blank') {
                metadata = {
                    answers: fillBlankAnswers,
                    marks: fillBlankMarks
                };
            } else if (questionType === 'matching') {
                metadata = {
                    pairs: matchingPairs.map(pair => ({
                        left: pair.left,
                        right: pair.right
                    }))
                };
            } else if (questionType === 'true_false') {
                metadata = {
                    answer: trueFalseAnswer === 'true'
                };
            }
            
            // Convert topics from comma-separated string to array
            const topics = topicsInput
                .split(',')
                .map(t => t.trim())
                .filter(t => t.length > 0);
            
            // Prepare image metadata
            const imageMetadata = exerciseImages.map(img => ({
                filename: img.file.name,
                size: img.file.size,
                caption: img.caption || ''
            }));
            
            // Prepare data structure
            const submitData = {
                source: {
                    type: sourceType,
                    name: sourceName,
                    details: {
                        chapter: sourceDetails.chapter || '',
                        page_number: sourceDetails.page_number || '',
                        year: sourceDetails.year || '',
                        semester: sourceDetails.semester || '',
                        institution: sourceDetails.institution || '',
                        notes: sourceDetails.notes || ''
                    }
                },
                exercise: {
                    question_type: questionType,
                    subject: subject,
                    grade: grade,
                    question_text: questionText,
                    difficulty: difficulty,
                    topics: topics,
                    reviewed: reviewed,
                    metadata: metadata
                },
                images: imageMetadata
            };
            
            // Get JWT token
            const token = localStorage.getItem('jwtToken');
            
            if (!token) {
                submitError = 'Authentication required. Please log in.';
                isSubmitting = false;
                return;
            }
            
            // Send POST request
            const response = await fetch('/studentplayground/api/exercise/submit-debug/', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(submitData)
            });
            
            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || data.message || 'Failed to submit exercise');
            }
            
            submitSuccess = 'Exercise submitted successfully! Check the Django console for received data.';
            
        } catch (error) {
            submitError = `Error submitting exercise: ${error.message}`;
            console.error('Submit error:', error);
        } finally {
            isSubmitting = false;
        }
    }
</script>

<div class="app-container">
    <!-- Sidebar -->
    <ExerciseBankSidebar {activePage} onPageChange={handlePageChange} />

    <!-- Main Content -->
    <main class="main-content">
        <div class="upload-exercise-container">
            <div class="form-section">
        <h2 class="section-title">A. Source Information</h2>
        <p class="section-description">Provide information about the source of the exercise.</p>

        <!-- Source Type Dropdown -->
        <Dropdown 
            label="Source Type *"
            options={sourceTypeOptions}
            bind:selectedValue={sourceType}
        />

        <!-- Source Name Input (only shown when source type is selected) -->
        {#if sourceType}
            <div class="form-field">
                <label for="source-name">Source Name *</label>
                <input
                    id="source-name"
                    type="text"
                    bind:value={sourceName}
                    placeholder="e.g., 'Physics 2023 Final' or 'Campbell Biology 12th Ed.'"
                    class="form-input"
                />
            </div>
        {/if}

        <!-- Dynamic Source Details Fields -->
        {#if sourceType}
            <div class="details-section">
                <h3 class="details-title">Source Details</h3>

                <!-- Textbook Fields -->
                {#if showTextbookFields}
                    <div class="form-field">
                        <label for="chapter">Chapter</label>
                        <input
                            id="chapter"
                            type="text"
                            bind:value={sourceDetails.chapter}
                            placeholder="e.g., 'Chapter 5' or '5'"
                            class="form-input"
                        />
                    </div>
                    <div class="form-field">
                        <label for="page-number">Page Number</label>
                        <input
                            id="page-number"
                            type="text"
                            bind:value={sourceDetails.page_number}
                            placeholder="e.g., '123' or '123-125'"
                            class="form-input"
                        />
                    </div>
                {/if}

                <!-- Exam Fields -->
                {#if showExamFields}
                    <div class="form-field">
                        <label for="year">Year</label>
                        <input
                            id="year"
                            type="text"
                            bind:value={sourceDetails.year}
                            placeholder="e.g., '2023'"
                            class="form-input"
                        />
                    </div>
                    <div class="form-field">
                        <label for="semester">Semester</label>
                        <input
                            id="semester"
                            type="text"
                            bind:value={sourceDetails.semester}
                            placeholder="e.g., 'Fall', 'Spring', '1st Semester'"
                            class="form-input"
                        />
                    </div>
                    <div class="form-field">
                        <label for="institution">Institution</label>
                        <input
                            id="institution"
                            type="text"
                            bind:value={sourceDetails.institution}
                            placeholder="e.g., 'Vilnius University', 'VGTU'"
                            class="form-input"
                        />
                    </div>
                {/if}

                <!-- Custom/Other/Worksheet Fields -->
                {#if showCustomFields}
                    <div class="form-field">
                        <label for="notes">Additional Notes (Optional)</label>
                        <textarea
                            id="notes"
                            bind:value={sourceDetails.notes}
                            placeholder="Any additional information about this source..."
                            class="form-textarea"
                            rows="3"
                        ></textarea>
                    </div>
                {/if}
            </div>
        {/if}
    </div>

    <!-- Exercise Section -->
    <div class="form-section">
        <h2 class="section-title">B. Exercise Information</h2>
        <p class="section-description">Provide details about the exercise question.</p>

        <!-- Question Type Dropdown -->
        <Dropdown 
            label="Question Type *"
            options={questionTypeOptions}
            bind:selectedValue={questionType}
        />

        <!-- Subject Dropdown -->
        <Dropdown 
            label="Subject *"
            options={subjectOptions}
            bind:selectedValue={subject}
        />

        <!-- Grade Dropdown -->
        <Dropdown 
            label="Grade *"
            options={gradeOptions}
            bind:selectedValue={grade}
        />

        <!-- Question Text -->
        {#if questionType}
            <div class="form-field">
                <label for="question-text">Question Text *</label>
                
                {#if useMathEditor}
                    <!-- Insert Math Button -->
                    <button 
                        type="button" 
                        class="insert-math-button"
                        on:click={openMathPanel}
                    >
                        <span class="button-icon">∫</span>
                        <span class="button-text">Insert Math Equation</span>
                    </button>
                {/if}
                
                <!-- Regular Textarea -->
                <textarea
                    id="question-text"
                    bind:this={questionTextareaRef}
                    bind:value={questionText}
                    placeholder="Enter the question prompt..."
                    class="form-textarea"
                    rows="6"
                    on:click={(e) => cursorPosition = e.target.selectionStart}
                    on:keyup={(e) => cursorPosition = e.target.selectionStart}
                ></textarea>
                
                <!-- Preview Section -->
                {#if useMathEditor && questionText}
                    <div class="preview-section">
                        <div class="preview-label">Preview:</div>
                        <div class="preview-content" id="question-preview">
                            {#each previewParts as part}
                                {#if part.type === 'math'}
                                    <span class="math-preview">{@html part.content}</span>
                                {:else}
                                    <span>{part.content}</span>
                                {/if}
                            {/each}
                        </div>
                    </div>
                {/if}
            </div>

            <!-- Difficulty -->
            <Dropdown 
                label="Difficulty *"
                options={difficultyOptions}
                bind:selectedValue={difficulty}
            />

            <!-- Topics -->
            <div class="form-field">
                <label for="topics">Topics *</label>
                <input
                    id="topics"
                    type="text"
                    bind:value={topicsInput}
                    placeholder="Enter topics separated by commas (e.g., 'Physics, Mechanics, Newton')"
                    class="form-input"
                />
                <small class="field-hint">Separate multiple topics with commas</small>
            </div>

            <!-- Reviewed Checkbox -->
            <div class="form-field checkbox-field">
                <label class="checkbox-label">
                    <input
                        type="checkbox"
                        bind:checked={reviewed}
                    />
                    <span>Mark as reviewed</span>
                </label>
            </div>

            <!-- Type-Specific Fields -->
            <div class="type-specific-section">
                <h3 class="details-title">Type-Specific Details</h3>

                <!-- Free Form Fields -->
                {#if showFreeFormFields}
                    <div class="form-field">
                        <label for="correct-answer">Correct Answer</label>
                        <textarea
                            id="correct-answer"
                            bind:value={correctAnswer}
                            placeholder="Enter the expected answer..."
                            class="form-textarea"
                            rows="3"
                        ></textarea>
                    </div>
                    <div class="form-field">
                        <label for="marks">Marks (Optional)</label>
                        <input
                            id="marks"
                            type="text"
                            bind:value={marks}
                            placeholder="e.g., '5'"
                            class="form-input"
                        />
                    </div>
                    <div class="form-field">
                        <label for="hints">Hints (Optional)</label>
                        <textarea
                            id="hints"
                            bind:value={hints}
                            placeholder="Enter hints for students..."
                            class="form-textarea"
                            rows="2"
                        ></textarea>
                    </div>
                {/if}

                <!-- Multiple Choice Fields -->
                {#if showMcqFields}
                    <div class="form-field checkbox-field">
                        <label class="checkbox-label">
                            <input
                                type="checkbox"
                                bind:checked={allowMultiple}
                            />
                            <span>Allow multiple correct answers</span>
                        </label>
                    </div>
                    <div class="form-field">
                        <label for="mcq-marks">Total Marks (Optional)</label>
                        <input
                            id="mcq-marks"
                            type="text"
                            bind:value={mcqMarks}
                            placeholder="e.g., '4'"
                            class="form-input"
                        />
                    </div>
                    <div class="options-section">
                        <div class="options-header">
                            <span class="section-label">Options *</span>
                            <button type="button" class="add-button" on:click={addMcqOption}>+ Add Option</button>
                        </div>
                        {#each mcqOptions as option, index}
                            <div class="option-item">
                                <input
                                    type="text"
                                    bind:value={option.text}
                                    placeholder={`Option ${index + 1}`}
                                    class="form-input option-input"
                                />
                                <button
                                    type="button"
                                    class="correct-button {option.is_correct ? 'active' : ''}"
                                    on:click={() => toggleMcqCorrect(index)}
                                >
                                    {option.is_correct ? '✓ Correct' : 'Mark Correct'}
                                </button>
                                {#if mcqOptions.length > 1}
                                    <button
                                        type="button"
                                        class="remove-button"
                                        on:click={() => removeMcqOption(index)}
                                    >
                                        Remove
                                    </button>
                                {/if}
                            </div>
                        {/each}
                    </div>
                {/if}

                <!-- Fill-in-the-Blank Fields -->
                {#if showFillBlankFields}
                    <div class="options-section">
                        <div class="options-header">
                            <span class="section-label">Blanks *</span>
                            <button type="button" class="add-button" on:click={addFillBlank}>+ Add Blank</button>
                        </div>
                        {#each fillBlankAnswers as answer, index}
                            <div class="blank-item">
                                <div class="form-field">
                                    <label for="blank-answer-{index}">Blank {index + 1} - Correct Answer</label>
                                    <input
                                        id="blank-answer-{index}"
                                        type="text"
                                        bind:value={fillBlankAnswers[index]}
                                        placeholder="Enter correct answer for this blank"
                                        class="form-input"
                                    />
                                </div>
                                <div class="form-field">
                                    <label for="blank-marks-{index}">Marks (Optional)</label>
                                    <input
                                        id="blank-marks-{index}"
                                        type="text"
                                        bind:value={fillBlankMarks[index]}
                                        placeholder="e.g., '2'"
                                        class="form-input"
                                    />
                                </div>
                                {#if fillBlankAnswers.length > 1}
                                    <button
                                        type="button"
                                        class="remove-button"
                                        on:click={() => removeFillBlank(index)}
                                    >
                                        Remove Blank
                                    </button>
                                {/if}
                            </div>
                        {/each}
                    </div>
                {/if}

                <!-- Matching Fields -->
                {#if showMatchingFields}
                    <div class="options-section">
                        <div class="options-header">
                            <span class="section-label">Matching Pairs *</span>
                            <button type="button" class="add-button" on:click={addMatchingPair}>+ Add Pair</button>
                        </div>
                        {#each matchingPairs as pair, index}
                            <div class="matching-item">
                                <div class="matching-pair">
                                    <div class="form-field">
                                        <label for="match-left-{index}">Left Item</label>
                                        <input
                                            id="match-left-{index}"
                                            type="text"
                                            bind:value={pair.left}
                                            placeholder="e.g., 'H2O'"
                                            class="form-input"
                                        />
                                    </div>
                                    <div class="form-field">
                                        <label for="match-right-{index}">Right Item</label>
                                        <input
                                            id="match-right-{index}"
                                            type="text"
                                            bind:value={pair.right}
                                            placeholder="e.g., 'Water'"
                                            class="form-input"
                                        />
                                    </div>
                                </div>
                                {#if matchingPairs.length > 1}
                                    <button
                                        type="button"
                                        class="remove-button"
                                        on:click={() => removeMatchingPair(index)}
                                    >
                                        Remove Pair
                                    </button>
                                {/if}
                            </div>
                        {/each}
                    </div>
                {/if}

                <!-- True/False Fields -->
                {#if showTrueFalseFields}
                    <div class="form-field">
                        <span class="section-label">Correct Answer *</span>
                        <div class="radio-group">
                            <label class="radio-label">
                                <input
                                    type="radio"
                                    bind:group={trueFalseAnswer}
                                    value="true"
                                />
                                <span>True</span>
                            </label>
                            <label class="radio-label">
                                <input
                                    type="radio"
                                    bind:group={trueFalseAnswer}
                                    value="false"
                                />
                                <span>False</span>
                            </label>
                        </div>
                    </div>
                {/if}
            </div>

            <!-- Exercise Images -->
            <div class="images-section">
                <h3 class="details-title">Exercise Images (Optional)</h3>
                <div class="form-field">
                    <label for="image-upload">Upload Images</label>
                    <input
                        id="image-upload"
                        type="file"
                        accept="image/*"
                        multiple
                        on:change={handleImageUpload}
                        class="file-input"
                    />
                    <small class="field-hint">You can upload multiple images</small>
                </div>
                
                {#if exerciseImages.length > 0}
                    <div class="images-grid">
                        {#each exerciseImages as image, index}
                            <div class="image-preview-item">
                                <img src={image.preview} alt="Preview" class="image-preview" />
                                <input
                                    type="text"
                                    bind:value={image.caption}
                                    placeholder="Image caption (optional)"
                                    class="form-input caption-input"
                                />
                                <button
                                    type="button"
                                    class="remove-button"
                                    on:click={() => removeImage(index)}
                                >
                                    Remove Image
                                </button>
                            </div>
                        {/each}
                    </div>
                {/if}
            </div>
            
            <!-- Submit Button -->
            <div class="submit-section">
                <button
                    type="button"
                    class="submit-button"
                    on:click={handleSubmit}
                    disabled={isSubmitting}
                >
                    {#if isSubmitting}
                        Submitting...
                    {:else}
                        Submit Exercise
                    {/if}
                </button>
                
                {#if submitError}
                    <div class="error-message">{submitError}</div>
                {/if}
                
                {#if submitSuccess}
                    <div class="success-message">{submitSuccess}</div>
                {/if}
            </div>
        {/if}
    </div>
    
    <!-- Math Panel Modal Dialog -->
    {#if showMathPanel}
        <!-- Backdrop -->
        <div 
            class="math-modal-backdrop" 
            on:click={(e) => {
                // Only close if clicking directly on backdrop, not on modal or keyboard
                if (e.target === e.currentTarget) {
                    closeMathPanel();
                }
            }}
            on:keydown={(e) => e.key === 'Escape' && closeMathPanel()}
            role="button"
            tabindex="0"
            aria-label="Close math panel"
        ></div>
        
        <!-- Centered Modal -->
        <div 
            class="math-modal" 
            on:keydown={(e) => e.key === 'Escape' && closeMathPanel()} 
            on:click={(e) => e.stopPropagation()}
            role="dialog" 
            aria-modal="true" 
            aria-labelledby="math-modal-title"
            tabindex="-1"
        >
            <div class="math-modal-header">
                <h3 id="math-modal-title" class="math-modal-title">Insert Math Equation</h3>
                <button type="button" class="math-modal-close" on:click={closeMathPanel} aria-label="Close">×</button>
            </div>
            
            <div class="math-modal-body">
                <div class="math-modal-editor">
                    <math-field
                        use:handleMathPanelFieldMount
                        value={mathPanelValue}
                        placeholder="Enter your math equation..."
                        virtual-keyboard-mode="manual"
                        virtual-keyboard-toggle="false"
                        default-mode="math"
                        virtual-keyboard-container="body"
                    ></math-field>
                </div>
            </div>
            
            <div class="math-modal-footer">
                <button 
                    type="button" 
                    class="math-modal-cancel"
                    on:click={closeMathPanel}
                >
                    Cancel
                </button>
                <button 
                    type="button" 
                    class="math-modal-insert"
                    on:click={insertMathIntoTextarea}
                >
                    Insert
                </button>
            </div>
        </div>
    {/if}
        </div>
    </main>
</div>

<style>
    .app-container {
        display: flex;
        height: 100vh;
        background-color: #f9fafb;
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100vw;
        z-index: 1000;
    }

    /* Main Content Styles */
    .main-content {
        flex: 1;
        overflow: auto;
    }

    .upload-exercise-container {
        max-width: 800px;
        margin: 0 auto;
        padding: 32px 24px;
        background-color: #f9fafb;
        min-height: 100vh;
    }

    .form-section {
        background-color: white;
        border-radius: 8px;
        padding: 32px;
        border: 1px solid #e5e7eb;
        box-shadow: 0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16);
    }

    .section-title {
        color: #111827;
        font-size: 24px;
        font-weight: 700;
        margin: 0 0 8px 0;
    }

    .section-description {
        color: #4b5563;
        font-size: 14px;
        margin: 0 0 24px 0;
        line-height: 1.5;
    }

    .form-field {
        display: flex;
        flex-direction: column;
        margin-bottom: 20px;
        width: 100%;
    }

    .form-field label {
        margin-bottom: 8px;
        font-size: 1.17em;
        font-weight: bold;
        color: #111827;
    }

    .form-input {
        width: 100%;
        padding: 12px 16px;
        height: 48px;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        font-size: 14px;
        font-family: inherit;
        background-color: #eeebdb;
        box-sizing: border-box;
        box-shadow: 0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16);
    }

    .form-input:focus {
        outline: none;
        border-color: #fab45a;
        box-shadow: 0 0 0 3px rgba(250, 180, 90, 0.1);
    }

    .form-textarea {
        width: 100%;
        padding: 12px 16px;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        font-size: 14px;
        font-family: inherit;
        background-color: #eeebdb;
        resize: vertical;
        box-shadow: 0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16);
    }

    .form-textarea:focus {
        outline: none;
        border-color: #fab45a;
        box-shadow: 0 0 0 3px rgba(250, 180, 90, 0.1);
    }

    .details-section {
        margin-top: 24px;
        padding-top: 24px;
        border-top: 1px solid #e5e7eb;
    }

    .details-title {
        color: #111827;
        font-size: 18px;
        font-weight: 600;
        margin: 0 0 16px 0;
    }

    .form-section + .form-section {
        margin-top: 32px;
    }

    .field-hint {
        color: #6b7280;
        font-size: 12px;
        margin-top: 4px;
        display: block;
    }

    .checkbox-field {
        margin-bottom: 16px;
    }

    .checkbox-label {
        display: flex;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        font-weight: normal;
    }

    .checkbox-label input[type="checkbox"] {
        width: 18px;
        height: 18px;
        cursor: pointer;
    }

    .type-specific-section {
        margin-top: 24px;
        padding-top: 24px;
        border-top: 1px solid #e5e7eb;
    }

    .options-section {
        margin-top: 16px;
    }

    .options-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
    }

    .section-label {
        font-size: 1.17em;
        font-weight: bold;
        color: #111827;
        margin: 0;
    }

    .add-button {
        padding: 8px 16px;
        background-color: #fab45a;
        color: #111827;
        border: 1px solid #d1d5db;
        border-radius: 6px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: background-color 0.2s ease;
    }

    .add-button:hover {
        background-color: #c98f43;
    }

    .option-item {
        display: flex;
        gap: 12px;
        align-items: flex-start;
        margin-bottom: 12px;
    }

    .option-input {
        flex: 1;
    }

    .correct-button {
        padding: 12px 16px;
        background-color: white;
        color: #111827;
        border: 1px solid #d1d5db;
        border-radius: 6px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        white-space: nowrap;
    }

    .correct-button:hover {
        background-color: #f3f4f6;
    }

    .correct-button.active {
        background-color: #10b981;
        color: white;
        border-color: #10b981;
    }

    .remove-button {
        padding: 12px 16px;
        background-color: #ef4444;
        color: white;
        border: 1px solid #ef4444;
        border-radius: 6px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: background-color 0.2s ease;
        white-space: nowrap;
    }

    .remove-button:hover {
        background-color: #dc2626;
    }

    .blank-item,
    .matching-item {
        padding: 16px;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        margin-bottom: 16px;
        background-color: #f9fafb;
    }

    .matching-pair {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
        margin-bottom: 12px;
    }

    .radio-group {
        display: flex;
        gap: 24px;
        margin-top: 8px;
    }

    .radio-label {
        display: flex;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        font-weight: normal;
    }

    .radio-label input[type="radio"] {
        width: 18px;
        height: 18px;
        cursor: pointer;
    }

    .images-section {
        margin-top: 24px;
        padding-top: 24px;
        border-top: 1px solid #e5e7eb;
    }

    .file-input {
        width: 100%;
        padding: 12px 16px;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        font-size: 14px;
        font-family: inherit;
        background-color: #eeebdb;
        box-sizing: border-box;
        box-shadow: 0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16);
        cursor: pointer;
    }

    .file-input:focus {
        outline: none;
        border-color: #fab45a;
        box-shadow: 0 0 0 3px rgba(250, 180, 90, 0.1);
    }

    .images-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
        gap: 16px;
        margin-top: 16px;
    }

    .image-preview-item {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 12px;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        background-color: white;
    }

    .image-preview {
        width: 100%;
        height: 200px;
        object-fit: contain;
        background-color: #f9fafb;
        border-radius: 4px;
    }

    .caption-input {
        margin-top: 8px;
    }

    .submit-section {
        margin-top: 32px;
        padding-top: 24px;
        border-top: 1px solid #e5e7eb;
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    .submit-button {
        padding: 14px 32px;
        background-color: #fab45a;
        color: #111827;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        font-size: 16px;
        font-weight: 600;
        cursor: pointer;
        transition: background-color 0.2s ease;
        align-self: flex-start;
    }

    .submit-button:hover:not(:disabled) {
        background-color: #c98f43;
    }

    .submit-button:disabled {
        background-color: #d1d5db;
        color: #6b7280;
        cursor: not-allowed;
    }

    .error-message {
        padding: 12px 16px;
        background-color: #fee2e2;
        color: #dc2626;
        border: 1px solid #fca5a5;
        border-radius: 6px;
        font-size: 14px;
    }

    .success-message {
        padding: 12px 16px;
        background-color: #d1fae5;
        color: #065f46;
        border: 1px solid #6ee7b7;
        border-radius: 6px;
        font-size: 14px;
    }

    /* Insert Math Button */
    .insert-math-button {
        width: 100%;
        padding: 14px 20px;
        margin-bottom: 12px;
        background-color: #fab45a;
        color: #111827;
        border: 2px solid #fab45a;
        border-radius: 8px;
        font-size: 15px;
        font-weight: 600;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        transition: all 0.2s ease;
        box-shadow: 0px 2px 4px rgba(250, 180, 90, 0.3);
    }

    .insert-math-button:hover {
        background-color: #c98f43;
        border-color: #c98f43;
        transform: translateY(-1px);
        box-shadow: 0px 4px 6px rgba(250, 180, 90, 0.4);
    }

    .button-icon {
        font-size: 20px;
        font-weight: bold;
    }

    .button-text {
        font-size: 15px;
    }

    /* Preview Section */
    .preview-section {
        margin-top: 16px;
        padding: 16px;
        background-color: #f9fafb;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
    }

    .preview-label {
        font-size: 14px;
        font-weight: 600;
        color: #6b7280;
        margin-bottom: 8px;
        display: block;
    }

    .preview-content {
        font-size: 14px;
        line-height: 1.6;
        color: #111827;
        min-height: 24px;
    }

    .math-preview {
        display: inline-block;
        margin: 0 2px;
    }

    /* Math Modal Dialog Styles */
    .math-modal-backdrop {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 1000;
        animation: fadeIn 0.2s ease;
    }

    .math-modal {
        position: fixed;
        top: 10%;
        left: 50%;
        transform: translateX(-50%);
        width: 90%;
        max-width: 600px;
        max-height: 50vh;
        background-color: white;
        border-radius: 12px;
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        z-index: 1001;
        display: flex;
        flex-direction: column;
        animation: modalFadeIn 0.3s ease;
        overflow: hidden;
    }
    
    /* Ensure MathLive virtual keyboard appears above modal and is positioned at bottom */
    /* Use the highest possible z-index and ensure it's in the right stacking context */
    :global(math-virtual-keyboard) {
        z-index: 2147483647 !important; /* Maximum z-index value */
        position: fixed !important;
        bottom: 0 !important;
        left: 0 !important;
        right: 0 !important;
        max-height: 45vh !important;
        background-color: #2d2d2d !important;
        pointer-events: auto !important;
    }
    
    /* Also target any wrapper elements MathLive might use */
    :global(math-field math-virtual-keyboard),
    :global(math-field::part(virtual-keyboard)) {
        z-index: 2147483647 !important;
    }
    
    /* Ensure modal doesn't create a new stacking context that traps keyboard */
    .math-modal {
        isolation: auto;
    }
    
    /* Prevent clicks inside modal from bubbling to backdrop */
    .math-modal * {
        pointer-events: auto;
    }

    @keyframes modalFadeIn {
        from {
            opacity: 0;
            transform: translateX(-50%) translateY(-10px);
        }
        to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
        }
    }

    @keyframes fadeIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }

    .math-modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px 24px;
        border-bottom: 1px solid #e5e7eb;
        flex-shrink: 0;
    }

    .math-modal-title {
        margin: 0;
        font-size: 20px;
        font-weight: 600;
        color: #111827;
    }

    .math-modal-close {
        background: none;
        border: none;
        font-size: 28px;
        color: #6b7280;
        cursor: pointer;
        padding: 0;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        transition: all 0.2s ease;
    }

    .math-modal-close:hover {
        background-color: #f3f4f6;
        color: #111827;
    }

    .math-modal-body {
        flex: 1;
        padding: 24px;
        overflow-y: auto;
        min-height: 200px;
    }

    .math-modal-editor {
        width: 100%;
    }

    .math-modal-editor math-field {
        width: 100%;
        min-height: 200px;
        padding: 12px 16px;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        font-size: 14px;
        background-color: #eeebdb;
        box-shadow: 0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16);
        display: block;
        box-sizing: border-box;
    }

    .math-modal-editor math-field:focus-within {
        outline: none;
        border-color: #fab45a;
        box-shadow: 0 0 0 3px rgba(250, 180, 90, 0.1);
    }
    
    /* Hide the virtual keyboard toggle button since keyboard opens automatically */
    .math-modal-editor math-field::part(virtual-keyboard-toggle) {
        display: none !important;
    }

    .math-modal-footer {
        display: flex;
        gap: 12px;
        padding: 16px 24px;
        border-top: 1px solid #e5e7eb;
        flex-shrink: 0;
    }

    .math-modal-cancel,
    .math-modal-insert {
        flex: 1;
        padding: 12px 20px;
        border-radius: 6px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .math-modal-cancel {
        background-color: white;
        color: #6b7280;
        border: 1px solid #d1d5db;
    }

    .math-modal-cancel:hover {
        background-color: #f9fafb;
        border-color: #9ca3af;
    }

    .math-modal-insert {
        background-color: #fab45a;
        color: #111827;
        border: 1px solid #fab45a;
    }

    .math-modal-insert:hover {
        background-color: #c98f43;
        border-color: #c98f43;
    }

    /* Responsive Design */
    @media (max-width: 768px) {
        .app-container {
            flex-direction: column;
        }

        .upload-exercise-container {
            padding: 16px;
        }

        .form-section {
            padding: 24px 16px;
        }

        .section-title {
            font-size: 20px;
        }

        .matching-pair {
            grid-template-columns: 1fr;
        }

        .images-grid {
            grid-template-columns: 1fr;
        }

        .option-item {
            flex-direction: column;
        }

        .correct-button,
        .remove-button {
            width: 100%;
        }

        .math-modal {
            width: 95%;
            max-height: 90vh;
        }

        .math-modal-body {
            padding: 16px;
        }

        .math-modal-editor math-field {
            min-height: 150px;
        }
        
        .math-modal-footer {
            padding: 12px 16px;
        }
    }
</style>


